import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { User, Users, ArrowRight, Sparkles } from 'lucide-react';
import { buildChallengesContextForAI } from '@/utils/buildChallengesContextForAI';
interface WellnessTemplate {
  id: string;
  title: string;
  short_label: string;
  category: string;
}

interface WellnessUserChallenge {
  id: string;
  custom_title: string | null;
  category: string;
  duration_days: number;
  start_date: string;
  end_date: string;
  status: string;
  reason_text: string | null;
  template: WellnessTemplate | null;
  completed_days: number;
}

interface GroupChallenge {
  id: string;
  title: string;
  description: string | null;
  challenge_type: string | null;
  category: string | null;
  status: string | null;
}

interface GroupChallengeParticipant {
  id: string;
  group_challenge: GroupChallenge | null;
  completion_status: string | null;
}

export const ChallengesSummaryPanel: React.FC = () => {
  const [personalChallenges, setPersonalChallenges] = useState<WellnessUserChallenge[]>([]);
  const [groupChallenges, setGroupChallenges] = useState<GroupChallengeParticipant[]>([]);
  const [therappiReflection, setTherappiReflection] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [reflectionLoading, setReflectionLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setReflectionLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        setReflectionLoading(false);
        return;
      }

      // 1) Personal wellness challenges + completed days
      const { data: wellnessChallenges, error: wcError } = await supabase
        .from('wellness_user_challenges')
        .select(
          `
            id,
            custom_title,
            category,
            duration_days,
            start_date,
            end_date,
            status,
            reason_text,
            wellness_challenge_templates (
              id,
              title,
              short_label,
              category
            )
          `
        )
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('start_date', { ascending: false });

      if (wcError) {
        console.error('Error fetching wellness_user_challenges', wcError);
        setError('There was a problem loading your personal challenges.');
      }

      let personal: WellnessUserChallenge[] = [];

      if (wellnessChallenges) {
        const challengeIds = wellnessChallenges.map((c: any) => c.id);

        let completedByChallenge = new Map<string, number>();

        if (challengeIds.length > 0) {
          const { data: checkins, error: ciError } = await supabase
            .from('wellness_challenge_checkins')
            .select('user_challenge_id, completed')
            .in('user_challenge_id', challengeIds);

          if (ciError) {
            console.error('Error fetching wellness_challenge_checkins', ciError);
          } else if (checkins) {
            for (const c of checkins) {
              if (!c.completed) continue;
              const current = completedByChallenge.get(c.user_challenge_id) || 0;
              completedByChallenge.set(c.user_challenge_id, current + 1);
            }
          }
        }

        personal = wellnessChallenges.map((row: any) => {
          const template = row.wellness_challenge_templates
            ? {
                id: row.wellness_challenge_templates.id,
                title: row.wellness_challenge_templates.title,
                short_label: row.wellness_challenge_templates.short_label,
                category: row.wellness_challenge_templates.category
              }
            : null;

          return {
            id: row.id,
            custom_title: row.custom_title,
            category: row.category,
            duration_days: row.duration_days,
            start_date: row.start_date,
            end_date: row.end_date,
            status: row.status,
            reason_text: row.reason_text,
            template,
            completed_days: completedByChallenge.get(row.id) || 0
          } as WellnessUserChallenge;
        });
      }

      // 2) Group / community challenges the user is part of
      const { data: participants, error: gpError } = await supabase
        .from('challenge_participants')
        .select(
          `
            id,
            completion_status,
            group_challenges (
              id,
              title,
              description,
              challenge_type,
              category,
              status
            )
          `
        )
        .eq('user_id', user.id);

      if (gpError) {
        console.error('Error fetching group challenge participants', gpError);
      }

      setPersonalChallenges(personal);
      setGroupChallenges((participants || []) as any);
      setLoading(false);

      // 3) Fetch Therappi Reflection
      try {
        const reflectionText = await buildChallengesContextForAI(user.id);
        setTherappiReflection(reflectionText);
      } catch (err) {
        console.error('Error building challenges context for AI', err);
      }
      setReflectionLoading(false);
    };

    fetchData();
  }, []);

  const goToCorrelations = () => {
    navigate('/vault?tab=correlations');
  };

  const hasAnyChallenges = personalChallenges.length > 0 || groupChallenges.length > 0;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-full mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-full mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        {/* My Personal Challenges */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              My Personal Challenges
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Gentle commitments you've made with yourself - for your sleep, nervous system, food, movement and more.
            </p>
          </CardHeader>
          <CardContent>
            {personalChallenges.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                You haven't started any personal challenges yet. You can begin with small steps, like sleep, screen time, or nervous system practices.
              </p>
            ) : (
              <ul className="space-y-3">
                {personalChallenges.slice(0, 3).map((c) => {
                  const title = c.custom_title || c.template?.short_label || c.template?.title || 'Personal challenge';
                  const progressPercent = c.duration_days > 0 
                    ? Math.round((c.completed_days / c.duration_days) * 100) 
                    : 0;
                  return (
                    <li
                      key={c.id}
                      onClick={() => navigate(`/challenge/${c.id}`)}
                      className="rounded-lg border border-border px-3 py-2.5 text-xs flex flex-col gap-1.5 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-foreground line-clamp-1">
                          {title}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {c.status}
                          </Badge>
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>{c.completed_days} / {c.duration_days} days</span>
                          <span>{progressPercent}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-1.5" />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Community Challenges */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-secondary" />
              Community Challenges
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Group challenges you're part of - shared journeys where others are walking alongside you.
            </p>
          </CardHeader>
          <CardContent>
            {groupChallenges.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                You're not in any community challenges right now. These are optional containers for shared focus and support.
              </p>
            ) : (
              <ul className="space-y-3">
                {groupChallenges.map((p) => {
                  const gc = p.group_challenge as any;
                  if (!gc) return null;
                  const status = p.completion_status || gc.status || 'active';
                  return (
                    <li
                      key={p.id}
                      className="rounded-lg border border-border px-3 py-2.5 text-xs flex flex-col gap-1.5 bg-muted/30"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-foreground line-clamp-1">
                          {gc.title}
                        </span>
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <Badge variant="secondary" className="text-[10px]">
                          {gc.category || 'Group reset'}
                        </Badge>
                        <span>•</span>
                        <span>With others</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Therappi Reflection */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Therappi Reflection on Your Challenges
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            A gentle summary of how your challenges connect to your wellbeing data.
          </p>
        </CardHeader>
        <CardContent>
          {reflectionLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-3 w-3/5" />
            </div>
          ) : hasAnyChallenges && therappiReflection ? (
            <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">
              {therappiReflection}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              You don't have any active challenges yet. When you're ready, you can create a gentle personal challenge to help your nervous system feel more supported.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChallengesSummaryPanel;
